import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../utils/api";
import { RiFileList3Fill } from "react-icons/ri";
import Modal from "../components/Modal";
import AddEarning from "../components/earning/AddEarning";
import EditEarning from "../components/earning/EditEarning";
import AddBtn from "../components/AddBtn";
import EditBtn from "../components/EditBtn";
import DeleteBtn from "../components/DeleteBtn";

export default function Earnings() {
  const { contentOwnerId } = useParams();
  const navigate = useNavigate();

  const [earnings, setEarnings] = useState([]);
  const [contentOwner, setContentOwner] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const itemsPerPage = 5;

  const [isModalOpenForAdd, setIsModalOpenForAdd] = useState(false);
  const [isModalOpenForEdit, setIsModalOpenForEdit] = useState(false);
  const [selectedEarning, setSelectedEarning] = useState(null);

  const fetchEarnings = async (page = 1, q = "") => {
    setLoading(true);
    try {
      const res = await api.get(`/earnings/content-owner/${contentOwnerId}`, {
        params: { page, limit: itemsPerPage, q },
        withCredentials: true,
      });
      setEarnings(res.data.earnings || []);

      if (res.data.pagination) {
        setTotalPages(res.data.pagination.totalPages || 1);
        setCurrentPage(res.data.pagination.currentPage || page);
      }
    } catch (err) {
      console.error("Error fetching earnings:", err);
      setEarnings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!contentOwnerId) return;
    api
      .get(`/contentOwners/${contentOwnerId}`, { withCredentials: true })
      .then((res) => {
        setContentOwner(res.data);
        if (res.data.productId) {
          api
            .get(`/products/${res.data.productId}`, { withCredentials: true })
            .then((productRes) => setProduct(productRes.data))
            .catch((err) => console.error("Error fetching product:", err));
        }
      })
      .catch((err) => console.error("Error fetching content owner:", err));
  }, [contentOwnerId]);

  useEffect(() => {
    api
      .get("/users/me", { withCredentials: true })
      .then((res) => setUserRole(res.data.role))
      .catch(() => setUserRole(null));
  }, []);

  useEffect(() => {
    if (!contentOwnerId) return;
    fetchEarnings(currentPage, search);
  }, [contentOwnerId, currentPage, search]);

  const deleteEarning = async (id) => {
    if (!window.confirm("Are you sure you want to delete this earning?"))
      return;

    try {
      await api.delete(`/earnings/${id}`, { withCredentials: true });
      if (earnings.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchEarnings(currentPage, search);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to delete earning!");
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch(searchInput);
  };

  const handleClear = () => {
    setSearchInput("");
    setCurrentPage(1);
    setSearch("");
  };

  const paginatedEarnings = earnings;

  return (
    <>
      <Modal
        isOpen={isModalOpenForAdd}
        onClose={() => setIsModalOpenForAdd(false)}
      >
        <AddEarning
          contentOwnerId={contentOwnerId}
          onSuccess={async () => {
            setIsModalOpenForAdd(false);
            setCurrentPage(1);
            fetchEarnings(1, search);
          }}
        />
      </Modal>

      <Modal
        isOpen={isModalOpenForEdit}
        onClose={() => setIsModalOpenForEdit(false)}
      >
        {selectedEarning && (
          <EditEarning
            earning={selectedEarning}
            onSuccess={async () => {
              setIsModalOpenForEdit(false);
              fetchEarnings(currentPage, search);
            }}
          />
        )}
      </Modal>

      <div>
        <div className="flex flex-col items-center mt-10 gap-2">
          {product && (
            <>
              <img
                src={`${process.env.REACT_APP_API_URL}/${product.imageUrl}`}
                alt={product.name}
                className="w-14 h-14 object-cover rounded-xl"
              />
              <h1 className="text-2xl font-bold">{product.name}</h1>
              <h2 className="text-xl font-semibold">Earnings</h2>
            </>
          )}
        </div>

        <div className="ml-2 mt-5 bg-white rounded-md shadow-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              {userRole && ["Admin", "Manager"].includes(userRole) && (
                <AddBtn
                  onClick={() => setIsModalOpenForAdd(true)}
                  label="Add Earning"
                />
              )}

              <button
                onClick={() =>
                  navigate(`/dashboard/content-owner/${contentOwnerId}/payouts`)
                }
                className="px-4 py-2 text-white font-medium rounded-lg transition bg-green-500 hover:bg-green-600 cursor-pointer flex items-center gap-2"
              >
                View Payouts
                <RiFileList3Fill className="w-4 h-4" />
              </button>
            </div>

            <div>
              {contentOwner && (
                <div className="text-gray-700">
                  Content Owner:{" "}
                  <span className="font-bold text-blue-600">
                    {contentOwner.name}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="mb-4 flex justify-end gap-2">
            <input
              type="date"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-40 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition"
            >
              Search
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-gray-400 text-white font-medium rounded-lg hover:bg-gray-500 transition"
            >
              Clear
            </button>
          </div>

          <table className="w-full text-sm border-separate border-spacing-y-2">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                <th className="px-4 py-3 text-left rounded-tl-xl rounded-bl-xl">
                  Date
                </th>
                <th className="px-4 py-3 text-center">Total Earning</th>
                <th className="px-4 py-3 text-center">Platform Commission</th>

                {userRole && ["Admin", "Manager"].includes(userRole) ? (
                  <>
                    <th className="px-4 py-3 text-center">Net Earning</th>
                    <th className="px-4 py-3 text-right rounded-tr-xl rounded-br-xl">
                      Actions
                    </th>
                  </>
                ) : (
                  <th className="px-4 py-3 text-center rounded-tr-xl rounded-br-xl">
                    Net Earning
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-4">
                    Loading earnings...
                  </td>
                </tr>
              ) : paginatedEarnings.length ? (
                paginatedEarnings.map((earning) => (
                  <tr
                    key={earning._id}
                    className="border-t transition hover:bg-slate-100"
                  >
                    <td className="px-4 py-4">
                      {new Date(earning.date).toLocaleDateString("en-GB")}
                    </td>
                    <td className="px-4 py-4 text-center">
                      ${earning.totalEarning}
                    </td>
                    <td className="px-4 py-4 text-center">
                      ${earning.platformCommission}
                    </td>
                    <td className="px-4 py-4 text-center font-semibold">
                      ${earning.netEarning}
                    </td>
                    {userRole && ["Admin", "Manager"].includes(userRole) && (
                      <td className="px-4 py-4 flex justify-end gap-4">
                        <EditBtn
                          onClick={() => {
                            setSelectedEarning(earning);
                            setIsModalOpenForEdit(true);
                          }}
                          label="Edit Earning"
                        />
                        <DeleteBtn
                          onClick={() => deleteEarning(earning._id)}
                          label="Delete Earning"
                        />
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-4">
                    No earnings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-1 mt-4">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>

              <span className="px-3 py-1 font-medium">
                {currentPage} / {totalPages}
              </span>

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
